import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Zap, Lock, Download, FileText, Image as ImageIcon, Film, FileArchive, File as FileIcon, Loader2, FolderOpen, Clock } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function fileIcon(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.includes("pdf")) return FileText;
  if (ct.includes("zip")) return FileArchive;
  return FileIcon;
}
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`);

export default function Portal() {
  const { token } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portal/${token}`);
      setGallery(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setVerifying(true);
    try {
      const { data } = await api.post(`/portal/${token}/access`, { code });
      setGallery(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Incorrect code.");
    } finally {
      setVerifying(false);
    }
  };

  const downloadUrl = (fid) => `${API}/portal/${token}/files/${fid}/download${code ? `?code=${encodeURIComponent(code)}` : ""}`;

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center gap-2.5 px-6 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary"><Zap className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} /></div>
          <span className="font-display text-base font-bold tracking-tight">BizForge<span className="text-accent">AI</span></span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">{children}</main>
    </div>
  );

  if (loading) return <Shell><div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-accent" /></div></Shell>;

  if (notFound) return (
    <Shell>
      <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="portal-not-found">
        <FolderOpen className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-4 font-display text-xl font-bold">Link not available</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">This portal link is invalid or has been removed.</p>
      </div>
    </Shell>
  );

  if (gallery.expired) return (
    <Shell>
      <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="portal-expired">
        <Clock className="mx-auto h-10 w-10 text-rose-500" />
        <h1 className="mt-4 font-display text-xl font-bold">This link has expired</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Please contact the sender for renewed access.</p>
      </div>
    </Shell>
  );

  // Code required and not yet unlocked
  if (gallery.requires_code && gallery.files.length === 0) return (
    <Shell>
      <div className="mx-auto max-w-sm rounded-2xl border border-border bg-card p-8" data-testid="portal-code-gate">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent"><Lock className="h-5 w-5" /></div>
        <h1 className="mt-4 font-display text-xl font-bold">{gallery.title}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">This gallery is protected. Enter the access code you were given.</p>
        <form onSubmit={submitCode} className="mt-5 space-y-3">
          <div className="space-y-1.5"><Label htmlFor="code">Access code</Label><Input id="code" value={code} onChange={(e) => setCode(e.target.value)} required data-testid="portal-code-input" /></div>
          {error && <p className="text-sm text-destructive" data-testid="portal-code-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={verifying} data-testid="portal-code-submit">{verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Unlock</Button>
        </form>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div data-testid="portal-gallery">
        <p className="text-xs font-semibold uppercase tracking-wider text-accent">Deliverables</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{gallery.title}</h1>
        {gallery.client_name && <p className="mt-1 text-muted-foreground">Prepared for {gallery.client_name}</p>}
        {gallery.description && <p className="mt-4 text-sm text-muted-foreground">{gallery.description}</p>}

        <div className="mt-8 space-y-2.5">
          {gallery.files.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="portal-empty">No files have been shared yet.</div>
          ) : gallery.files.map((f) => {
            const Icon = fileIcon(f.content_type);
            return (
              <div key={f.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`portal-file-${f.id}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary"><Icon className="h-5 w-5 text-muted-foreground" /></div>
                <div className="min-w-0 flex-1"><p className="truncate font-medium">{f.name}</p><p className="text-xs text-muted-foreground">{fmtSize(f.size)}</p></div>
                <a href={downloadUrl(f.id)} download target="_blank" rel="noopener noreferrer" data-testid={`portal-download-${f.id}`}>
                  <Button size="sm"><Download className="mr-1.5 h-4 w-4" /> Download</Button>
                </a>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground">Delivered securely via BizForge AI</p>
      </div>
    </Shell>
  );
}
